import React, { useEffect, useMemo, useState } from "react";
import {
    Search,
    RefreshCw,
    MessageCircle,
    Mail,
    Phone,
    Building2,
    UserRound,
    CalendarClock,
    CheckCircle2,
    Clock3,
    Star,
    CalendarCheck,
    XCircle,
    ExternalLink,
    ArrowRightCircle,
    Eye,
    CheckCheck,
} from "lucide-react";
import { API_BASE } from "../config/orbisConfig";



const STATUS_OPTIONS = [
    { value: "", label: "Todos" },
    { value: "nuevo", label: "Nuevo" },
    { value: "contactado", label: "Contactado" },
    { value: "calificado", label: "Calificado" },
    { value: "demo_agendada", label: "Demo agendada" },
    { value: "convertido", label: "Convertido" },
    { value: "descartado", label: "Descartado" },
];

const STATUS_META = {
    nuevo: {
        label: "Nuevo",
        icon: Clock3,
        className: "border-amber-400/40 bg-amber-500/10 text-amber-100",
    },
    contactado: {
        label: "Contactado",
        icon: CheckCircle2,
        className: "border-sky-400/40 bg-sky-500/10 text-sky-100",
    },
    calificado: {
        label: "Calificado",
        icon: Star,
        className: "border-emerald-400/40 bg-emerald-500/10 text-emerald-100",
    },
    demo_agendada: {
        label: "Demo agendada",
        icon: CalendarCheck,
        className: "border-[#D4AF37]/50 bg-[#D4AF37]/10 text-[#F8E5A4]",
    },
    convertido: {
        label: "Convertido",
        icon: CheckCheck,
        className: "border-violet-400/40 bg-violet-500/10 text-violet-100",
    },
    descartado: {
        label: "Descartado",
        icon: XCircle,
        className: "border-red-400/40 bg-red-500/10 text-red-100",
    },
};

function normalizeMxPhone(phone) {
    if (!phone) return "";
    const digits = String(phone).replace(/\D+/g, "");
    if (!digits) return "";
    if (digits.length === 10) return `52${digits}`;
    return digits;
}

function buildWhatsAppLink(phone, name) {
    const waPhone = normalizeMxPhone(phone);
    if (!waPhone) return "";

    const text = `Hola ${name || ""}, gracias por solicitar acceso a ORBIS PMO.

Me gustaría coordinar contigo una demostración guiada para mostrarte cómo la plataforma ayuda a controlar proyectos, evidencias, riesgos, responsables y decisiones.

¿Qué horario te funciona mejor?`;

    return `https://wa.me/${waPhone}?text=${encodeURIComponent(text)}`;
}

function getUserId() {
    return Number(localStorage.getItem("user_id")) || Number(localStorage.getItem("id")) || null;
}

function authHeaders() {
    const token = localStorage.getItem("cani_auth") || localStorage.getItem("token") || "";

    return {
        "Content-Type": "application/json",
        Authorization: token ? `Bearer ${token}` : "",
        "X-CANI-AUTH": token || "",
    };
}

function StatusBadge({ status }) {
    const meta = STATUS_META[status] || STATUS_META.nuevo;
    const Icon = meta.icon;

    return (
        <span className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-bold ${meta.className}`}>
            <Icon size={14} />
            {meta.label}
        </span>
    );
}

function StatCard({ label, value, icon: Icon }) {
    return (
        <div className="rounded-3xl border border-[#275A7A]/70 bg-[#08233D]/70 p-5 shadow-[0_18px_55px_rgba(0,0,0,.20)]">
            <div className="flex items-center justify-between">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">{label}</p>
                <Icon className="text-[#D4AF37]" size={20} />
            </div>
            <p className="mt-3 text-3xl font-black text-white">{value || 0}</p>
        </div>
    );
}

export default function OrbisDemoRequestsPage() {
    const [items, setItems] = useState([]);
    const [counts, setCounts] = useState({});
    const [status, setStatus] = useState("");
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(false);
    const [updatingId, setUpdatingId] = useState(null);
    const [convertingId, setConvertingId] = useState(null);
    const [error, setError] = useState("");
    const [successMsg, setSuccessMsg] = useState("");

    const queryString = useMemo(() => {
        const params = new URLSearchParams();
        if (status) params.set("status", status);
        if (search.trim()) params.set("search", search.trim());
        return params.toString();
    }, [status, search]);

    async function loadRequests() {
        try {
            setLoading(true);
            setError("");

            const url = `${API_BASE}/orbis_demo_requests_list.php${queryString ? `?${queryString}` : ""}`;
            const res = await fetch(url, {
                headers: {
                    Authorization: localStorage.getItem("cani_auth") ? `Bearer ${localStorage.getItem("cani_auth")}` : "",
                    "X-CANI-AUTH": localStorage.getItem("cani_auth") || "",
                },
            });

            const json = await res.json();

            if (!res.ok || !json.success) {
                throw new Error(json.message || "No fue posible cargar solicitudes demo.");
            }

            setItems(json.data?.items || []);
            setCounts(json.data?.counts || {});
        } catch (err) {
            setError(err.message || "Error al cargar solicitudes demo.");
        } finally {
            setLoading(false);
        }
    }

    async function updateStatus(id, nextStatus) {
        try {
            setUpdatingId(id);
            setError("");
            setSuccessMsg("");

            const res = await fetch(`${API_BASE}/orbis_demo_request_update_status.php`, {
                method: "POST",
                headers: authHeaders(),
                body: JSON.stringify({
                    id,
                    status: nextStatus,
                }),
            });

            const json = await res.json();

            if (!res.ok || !json.success) {
                throw new Error(json.message || "No fue posible actualizar estatus.");
            }

            setSuccessMsg("Estatus actualizado correctamente.");
            await loadRequests();
        } catch (err) {
            setError(err.message || "Error al actualizar estatus.");
        } finally {
            setUpdatingId(null);
        }
    }

    async function convertToCrm(item) {
        const ok = window.confirm(
            `¿Convertir esta solicitud en cuenta, contacto, oportunidad y actividad CRM?\n\n${item.full_name}\n${item.company_name}`
        );

        if (!ok) return;

        try {
            setConvertingId(Number(item.id));
            setError("");
            setSuccessMsg("");

            const res = await fetch(`${API_BASE}/orbis_demo_request_convert_to_crm.php`, {
                method: "POST",
                headers: authHeaders(),
                body: JSON.stringify({
                    demo_request_id: Number(item.id),
                    converted_by: getUserId(),
                }),
            });

            const json = await res.json();

            if (!res.ok || !json.success) {
                throw new Error(json.message || json.error || "No fue posible convertir la solicitud al CRM.");
            }

            const opportunityId = json.data?.opportunity_id;
            setSuccessMsg(
                opportunityId
                    ? `Solicitud convertida correctamente. Oportunidad #${opportunityId} creada.`
                    : "Solicitud convertida correctamente."
            );

            await loadRequests();
        } catch (err) {
            setError(err.message || "Error al convertir solicitud al CRM.");
        } finally {
            setConvertingId(null);
        }
    }

    useEffect(() => {
        loadRequests();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [queryString]);

    return (
        <div className="min-h-screen bg-[#041426] px-6 py-8 text-white">
            <div className="mx-auto max-w-7xl">
                <div className="flex flex-col justify-between gap-5 md:flex-row md:items-end">
                    <div>
                        <p className="mb-3 inline-flex items-center gap-3 text-xs font-bold uppercase tracking-[0.24em] text-[#D4AF37]">
                            <span className="h-px w-10 bg-[#D4AF37]" />
                            CRM Comercial
                        </p>
                        <h1 className="text-4xl font-black tracking-tight md:text-5xl">
                            Leads Web ORBIS
                        </h1>
                        <p className="mt-4 max-w-3xl text-sm leading-6 text-slate-300">
                            Bandeja de entrada para solicitudes recibidas desde orbispmo.com. Convierte leads calificados en cuenta, contacto, oportunidad y actividad comercial.
                        </p>
                    </div>

                    <button
                        onClick={loadRequests}
                        disabled={loading}
                        className="inline-flex items-center justify-center gap-2 rounded-full border border-[#2B638E] bg-white/5 px-5 py-3 text-sm font-bold text-white hover:bg-white/10 disabled:opacity-60"
                    >
                        <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
                        Actualizar
                    </button>
                </div>

                <div className="mt-8 grid gap-4 md:grid-cols-3 lg:grid-cols-7">
                    <StatCard label="Total" value={counts.total} icon={Building2} />
                    <StatCard label="Nuevos" value={counts.nuevo} icon={Clock3} />
                    <StatCard label="Contactados" value={counts.contactado} icon={CheckCircle2} />
                    <StatCard label="Calificados" value={counts.calificado} icon={Star} />
                    <StatCard label="Demos" value={counts.demo_agendada} icon={CalendarCheck} />
                    <StatCard label="Convertidos" value={counts.convertido} icon={CheckCheck} />
                    <StatCard label="Descartados" value={counts.descartado} icon={XCircle} />
                </div>

                <div className="mt-8 rounded-3xl border border-[#275A7A]/70 bg-[#08233D]/70 p-5 shadow-[0_18px_55px_rgba(0,0,0,.20)]">
                    <div className="grid gap-4 md:grid-cols-[1fr_240px]">
                        <div className="relative">
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
                            <input
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Buscar por nombre, empresa, email, problema u objetivo..."
                                className="w-full rounded-2xl border border-[#24587C] bg-white/5 py-3 pl-11 pr-4 text-sm text-white outline-none placeholder:text-slate-500 focus:border-[#D4AF37]"
                            />
                        </div>

                        <select
                            value={status}
                            onChange={(e) => setStatus(e.target.value)}
                            className="rounded-2xl border border-[#24587C] bg-[#061A2F] px-4 py-3 text-sm text-white outline-none focus:border-[#D4AF37]"
                        >
                            {STATUS_OPTIONS.map((option) => (
                                <option key={option.value || "all"} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>

                    {successMsg ? (
                        <div className="mt-5 rounded-2xl border border-emerald-400/40 bg-emerald-500/10 p-4 text-sm text-emerald-100">
                            {successMsg}
                        </div>
                    ) : null}

                    {error ? (
                        <div className="mt-5 rounded-2xl border border-red-400/40 bg-red-500/10 p-4 text-sm text-red-100">
                            {error}
                        </div>
                    ) : null}

                    <div className="mt-6 overflow-hidden rounded-2xl border border-[#24587C]">
                        <div className="hidden grid-cols-[90px_1.15fr_1.1fr_1fr_1fr_150px_260px] gap-4 bg-[#061A2F] px-4 py-3 text-xs font-bold uppercase tracking-[0.14em] text-slate-400 lg:grid">
                            <div>ID</div>
                            <div>Contacto</div>
                            <div>Empresa</div>
                            <div>Problema</div>
                            <div>Fecha</div>
                            <div>Estatus</div>
                            <div>Acciones</div>
                        </div>

                        {loading ? (
                            <div className="p-8 text-center text-sm text-slate-300">
                                Cargando leads web ORBIS...
                            </div>
                        ) : items.length === 0 ? (
                            <div className="p-8 text-center text-sm text-slate-300">
                                No hay solicitudes demo con los filtros actuales.
                            </div>
                        ) : (
                            <div className="divide-y divide-[#24587C]">
                                {items.map((item) => {
                                    const whatsappLink = buildWhatsAppLink(item.phone, item.full_name);
                                    const disabled = updatingId === Number(item.id) || convertingId === Number(item.id);
                                    const converted = item.status === "convertido" || Number(item.opportunity_id || 0) > 0;

                                    return (
                                        <div
                                            key={item.id}
                                            className="grid gap-4 px-4 py-5 text-sm hover:bg-white/[0.03] lg:grid-cols-[90px_1.15fr_1.1fr_1fr_1fr_150px_260px]"
                                        >
                                            <div>
                                                <p className="text-xs uppercase tracking-[0.16em] text-slate-500 lg:hidden">ID</p>
                                                <p className="font-bold text-[#D4AF37]">#{item.id}</p>
                                            </div>

                                            <div>
                                                <p className="text-xs uppercase tracking-[0.16em] text-slate-500 lg:hidden">Contacto</p>
                                                <div className="flex items-start gap-3">
                                                    <UserRound className="mt-1 text-[#38BDF8]" size={18} />
                                                    <div>
                                                        <p className="font-bold text-white">{item.full_name}</p>
                                                        <p className="mt-1 flex items-center gap-2 text-xs text-slate-300">
                                                            <Mail size={13} /> {item.email || "Sin email"}
                                                        </p>
                                                        <p className="mt-1 flex items-center gap-2 text-xs text-slate-300">
                                                            <Phone size={13} /> {item.phone || "Sin celular"}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>

                                            <div>
                                                <p className="text-xs uppercase tracking-[0.16em] text-slate-500 lg:hidden">Empresa</p>
                                                <p className="font-semibold text-white">{item.company_name || "Sin empresa"}</p>
                                                <p className="mt-1 text-xs text-slate-400">{item.position_name || "Sin puesto"}</p>
                                                <p className="mt-2 line-clamp-2 text-xs leading-5 text-slate-300">
                                                    {item.company_activity || "Sin actividad especificada"}
                                                </p>
                                                {converted && item.crm_opportunity_name ? (
                                                    <p className="mt-2 text-xs font-bold text-violet-200">
                                                        {item.crm_opportunity_name}
                                                    </p>
                                                ) : null}
                                            </div>

                                            <div>
                                                <p className="text-xs uppercase tracking-[0.16em] text-slate-500 lg:hidden">Problema</p>
                                                <p className="line-clamp-3 text-xs leading-5 text-slate-300">
                                                    {item.main_problem || "Sin problema especificado"}
                                                </p>
                                            </div>

                                            <div>
                                                <p className="text-xs uppercase tracking-[0.16em] text-slate-500 lg:hidden">Fecha</p>
                                                <p className="flex items-center gap-2 text-xs text-slate-300">
                                                    <CalendarClock size={14} />
                                                    {item.created_at_human || item.created_at}
                                                </p>
                                                {item.converted_at_human ? (
                                                    <p className="mt-2 text-xs text-violet-200">
                                                        Convertido: {item.converted_at_human}
                                                    </p>
                                                ) : null}
                                            </div>

                                            <div>
                                                <p className="mb-2 text-xs uppercase tracking-[0.16em] text-slate-500 lg:hidden">Estatus</p>
                                                <StatusBadge status={item.status} />
                                            </div>

                                            <div className="flex flex-col gap-2">
                                                {converted ? (
                                                    <a
                                                        href={`/PM/opportunities/${item.opportunity_id}`}
                                                        className="inline-flex items-center justify-center gap-2 rounded-full bg-violet-500/90 px-4 py-2 text-xs font-black text-white hover:scale-[1.01]"
                                                    >
                                                        <Eye size={15} />
                                                        Ver oportunidad
                                                        <ExternalLink size={13} />
                                                    </a>
                                                ) : (
                                                    <button
                                                        disabled={disabled}
                                                        onClick={() => convertToCrm(item)}
                                                        className="inline-flex items-center justify-center gap-2 rounded-full bg-[#D4AF37] px-4 py-2 text-xs font-black text-[#061A2F] hover:scale-[1.01] disabled:opacity-50"
                                                    >
                                                        <ArrowRightCircle size={15} />
                                                        {convertingId === Number(item.id) ? "Convirtiendo..." : "Convertir a CRM"}
                                                    </button>
                                                )}

                                                {whatsappLink ? (
                                                    <a
                                                        href={whatsappLink}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        className="inline-flex items-center justify-center gap-2 rounded-full border border-[#2B638E] bg-white/5 px-4 py-2 text-xs font-bold text-white hover:bg-white/10"
                                                    >
                                                        <MessageCircle size={15} />
                                                        WhatsApp
                                                        <ExternalLink size={13} />
                                                    </a>
                                                ) : null}

                                                {!converted ? (
                                                    <div className="grid grid-cols-2 gap-2">
                                                        <button
                                                            disabled={disabled}
                                                            onClick={() => updateStatus(item.id, "contactado")}
                                                            className="rounded-full border border-[#2B638E] px-3 py-2 text-xs font-bold text-white hover:bg-white/10 disabled:opacity-50"
                                                        >
                                                            Contactado
                                                        </button>
                                                        <button
                                                            disabled={disabled}
                                                            onClick={() => updateStatus(item.id, "calificado")}
                                                            className="rounded-full border border-emerald-400/40 px-3 py-2 text-xs font-bold text-emerald-100 hover:bg-emerald-500/10 disabled:opacity-50"
                                                        >
                                                            Calificar
                                                        </button>
                                                        <button
                                                            disabled={disabled}
                                                            onClick={() => updateStatus(item.id, "demo_agendada")}
                                                            className="rounded-full border border-[#D4AF37]/50 px-3 py-2 text-xs font-bold text-[#F8E5A4] hover:bg-[#D4AF37]/10 disabled:opacity-50"
                                                        >
                                                            Demo
                                                        </button>
                                                        <button
                                                            disabled={disabled}
                                                            onClick={() => updateStatus(item.id, "descartado")}
                                                            className="rounded-full border border-red-400/40 px-3 py-2 text-xs font-bold text-red-100 hover:bg-red-500/10 disabled:opacity-50"
                                                        >
                                                            Descartar
                                                        </button>
                                                    </div>
                                                ) : null}
                                            </div>

                                            {item.demo_objective ? (
                                                <div className="rounded-2xl border border-[#24587C] bg-white/5 p-4 lg:col-span-7">
                                                    <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#D4AF37]">
                                                        Objetivo de ver ORBIS PMO
                                                    </p>
                                                    <p className="mt-2 text-sm leading-6 text-slate-300">
                                                        {item.demo_objective}
                                                    </p>
                                                </div>
                                            ) : null}
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
